<template>
  <div>
    <page-header>
      <h1 v-if="quoteSession.ashp">Explore the impact of Air-Source Heat Pumps</h1>
      <h1 v-else-if="quoteSession.solar">Explore the impact of Solar and Battery</h1>
      <h1 v-else-if="quoteSession.evCompare || quoteSession.evOnly">Explore the benefit of adopting an EV</h1>
    </page-header>

    <div class="container page-content">
      <form class="form-horizontal">
        <div class="row">
          <div class="col-md-offset-2 col-md-8 _margin-top-20">
            <div class="card question-card">
              <h6 class="text-center center-block _margin-bottom-10">Step {{ steps.current }} of {{ steps.outOf }}</h6>

              <div class="steps">
                <div v-for="s in Array.from(Array(steps.outOf).keys())" :class="{ active: s < steps.current }"></div>
              </div>

              <uiv-tabs v-model="step.tabIndex" ref="tabs" class="tabs--hide">
                <uiv-tab title="address">
                  <your-home :$v="$v.form"></your-home>
                </uiv-tab>

                <uiv-tab title="propertyId">
                  <property-type :$v="$v.form"></property-type>
                </uiv-tab>

                <uiv-tab title="propertyUsage">
                  <property-usage :$v="$v.form"></property-usage>
                </uiv-tab>

                <uiv-tab title="electricVehicleOwnership">
                  <electric-vehicle-ownership :$v="$v.form"></electric-vehicle-ownership>
                </uiv-tab>

                <uiv-tab title="heatingInsulation">
                  <heating-insulation :$v="$v.form"></heating-insulation>
                </uiv-tab>

                <uiv-tab title="iceVehicle">
                  <ice-vehicle :$v="$v.form"></ice-vehicle>
                </uiv-tab>

                <uiv-tab title="electricVehicle">
                  <electric-vehicle :$v="$v.form"></electric-vehicle>
                </uiv-tab>

                <uiv-tab title="vehicleUsage">
                  <vehicle-usage-distance :$v="$v.form"></vehicle-usage-distance>
                </uiv-tab>
              </uiv-tabs>
            </div>
            <uiv-alert type="danger" v-if="$v.form.$error">
              <b>Error</b> <br />
              Please ensure you answered all required fields
            </uiv-alert>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import YourHome from '@/components/current-tariff/YourHome';
import IceVehicle from '@/components/current-tariff/vehicle/IceVehicle';
import ElectricVehicle from '@/components/current-tariff/vehicle/ElectricVehicle';
import VehicleUsageDistance from '@/components/current-tariff/vehicle/VehicleUsageDistance';
import PropertyType from '@/components/current-tariff/PropertyType';
import PropertyUsage from '@/components/current-tariff/PropertyUsage';
import ElectricVehicleOwnership from '@/components/current-tariff/ElectricVehicleOwnership';
import HeatingInsulation from '@/components/current-tariff/air-source-heat-pump/HeatingInsulation';

import PageHeader from '@/components/page-header/PageHeader';
import ProgressBar from '@/components/progress/ProgressBar';

import { mapState, mapGetters } from 'vuex';

import {
  required, helpers, requiredIf, maxLength,
  minValue, maxValue
} from 'vuelidate/lib/validators';

export default {
  name: 'CurrentTariff',
  data() {
    return {
      form: {
        iceVehicle: {},
        electricVehicle: {},
        vehicleUsage: {},
        address: {},
        gasUsage: {},
        electricityUsage: {},
        tariff: {},
        gasTariff: {},
        electricityTariff: {}
      },
      costConditions: [null, { min: 120, max: 9000 }, { min: 10, max: 750 }, { min: 30, max: 2250 }],
      electricityUsageConditions: [null, { min: 1, max: 75000 }, { min: 1, max: 6250 }, { min: 1, max: 18750 }],
      gasUsageConditions: [null, { min: 1, max: 200000 }, { min: 1, max: 16600 }, { min: 1, max: 50000 }],
      error: null
    };
  },
  async mounted() {
    const excludeRoutes = ['home', 'solarBattery', 'electricVehicle', 'heatPump'];

    if (!excludeRoutes.includes(this.history.name)) {
      this.$store.commit('quote/resetState');
      this.$store.commit('lookups/resetState');
      this.$store.dispatch('storage/reset');
    }

    this.$store.dispatch('quote_questions/getQuestions', this.$refs.tabs.$children);
    this.$store.commit('quote_questions/restartJourney');

    this.form = this.quote;

    this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Viewed' });
    await this.$store.dispatch('tracing/saveActions');

    if (this.quoteSession.evOnly || this.quoteSession.evCompare) {
      this.$store.dispatch('lookups/getVehicleChargeTypes');
    }

    if (this.quoteSession.solar || this.quoteSession.ashp) {
      this.$store.dispatch('lookups/getEsaPropertyList');
    }

    if (this.quoteSession.ashp) {
      this.$store.dispatch('lookups/getInsulationList');
      this.$store.dispatch('lookups/getHeatingTypeList');
    }
  },
  validations() {
    return {
      form: {
        postcode: {
          required,
          maxLength: maxLength(7),
          regex: helpers.regex('postcode', /^(([gG][iI][rR] {0,}0[aA]{2})|((([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y]?[0-9][0-9]?)|(([a-pr-uwyzA-PR-UWYZ][0-9][a-hjkstuwA-HJKSTUW])|([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y][0-9][abehmnprv-yABEHMNPRV-Y]))) {0,}[0-9][abd-hjlnp-uw-zABD-HJLNP-UW-Z]{2}))$/)
        },
        address: {
          required,
          domesticMeter: (value) => {
            if (!value.parts) return false;
            const address = value.parts;
            const elecdetails = address.elecdetails.find(({ isDomestic }) => isDomestic) || {};
            const gasdetails = address.gasdetails.find(({ isDomestic }) => isDomestic) || {};
            const isDomesticE = !!elecdetails?.isDomestic;
            const isDomesticG = !!gasdetails?.isDomestic;

            return isDomesticE || isDomesticG;
          },
          gasOnly: (value) => {
            if (!value.parts) return false;
            return this.quoteType !== 'gasToGas';
          },
          prePaymentMeter: (value) => {
            if (!value.parts) return false;
            const address = value.parts;
            const elecdetails = address.elecdetails.find(({ isDomestic }) => isDomestic) || {};
            const gasdetails = address.gasdetails.find(({ isDomestic }) => isDomestic) || {};

            return [elecdetails.isPrepayment, gasdetails.isPrepayment].indexOf(true) < 0;
          },
          noElectricMeterData: (value) => {
            if (!value.parts) return false;
            const address = value.parts;

            return address.elecdetails.length != 0;
          }
        },
        iceVehicle: {
          registrationNumber: {
            requiredIf: requiredIf(() => !this.quote.iceVehicle.dontKnow && this.quoteSession.evCompare),
            regex: helpers.regex('registrationNumber', /(^[A-Z]{2}[0-9]{2}\s?[A-Z]{3}$)|(^[A-Z][0-9]{1,3}[A-Z]{3}$)|(^[A-Z]{3}[0-9]{1,3}[A-Z]$)|(^[0-9]{1,4}[A-Z]{1,2}$)|(^[0-9]{1,3}[A-Z]{1,3}$)|(^[A-Z]{1,2}[0-9]{1,4}$)|(^[A-Z]{1,3}[0-9]{1,3}$)|(^[A-Z]{1,3}[0-9]{1,4}$)|(^[0-9]{3}[DX]{1}[0-9]{3}$)/)
          },
          makeId: { requiredIf: requiredIf(() => this.quoteSession.evCompare) },
          modelId: { requiredIf: requiredIf(() => this.quoteSession.evCompare) }
        },
        electricVehicle: {
          registrationNumber: {
            requiredIf: requiredIf(() => {
              return !this.quote.electricVehicle.dontKnow && this.quoteSession.evOnly || !this.quote.electricVehicle.dontKnow && this.quoteSession.evCompare;
            }),
            regex: helpers.regex('registrationNumber', /(^[A-Z]{2}[0-9]{2}\s?[A-Z]{3}$)|(^[A-Z][0-9]{1,3}[A-Z]{3}$)|(^[A-Z]{3}[0-9]{1,3}[A-Z]$)|(^[0-9]{1,4}[A-Z]{1,2}$)|(^[0-9]{1,3}[A-Z]{1,3}$)|(^[A-Z]{1,2}[0-9]{1,4}$)|(^[A-Z]{1,3}[0-9]{1,3}$)|(^[A-Z]{1,3}[0-9]{1,4}$)|(^[0-9]{3}[DX]{1}[0-9]{3}$)/)
          },
          makeId: { requiredIf: requiredIf(() => this.quoteSession.evOnly || this.quoteSession.evCompare) },
          modelId: { requiredIf: requiredIf(() => this.quoteSession.evOnly || this.quoteSession.evCompare) }
        },
        vehicleUsage: {
          weekday: {
            requiredIf: requiredIf(() => this.quoteSession.evOnly || this.quoteSession.evCompare),
            minValue: minValue(0),
            maxValue: maxValue(4200)
          },
          weekend: {
            requiredIf: requiredIf(() => this.quoteSession.evOnly || this.quoteSession.evCompare),
            minValue: minValue(0),
            maxValue: maxValue(1680)
          }
        },
        propertyId: {
          requiredIf: requiredIf(() => {
            return this.quoteSession.ashp || this.quoteSession.solar;
          }),
          validProperty: (value) => {
            if (this.quoteSession.solar) {
              return value != 7 && value != 8;
            }

            return true;
          }
        },
        propertyUsage: {
          requiredIf: requiredIf(form => this.quoteSession.solar)
        },
        electricVehicleOwnership: {
          requiredIf: requiredIf(form => this.quoteSession.solar)
        },
        insulation: { requiredIf: requiredIf(form => this.quoteSession.ashp) },
        heatingFuel: { requiredIf: requiredIf(form => this.quoteSession.ashp) }
      }
    };
  },
  computed: {
    ...mapGetters({
      journey: 'quote_questions/journey',
      progress: 'quote_questions/progress',
      steps: 'quote_questions/steps',
      quoteType: 'quote/quoteType',
      is02Meter: 'quote/is02Meter',
      noMpan: 'quote/noMpan',
      nonDomesticMeter: 'quote/nonDomesticMeter'
    }),
    ...mapState({
      history: ({ history }) => history.previousRoute,
      quote: ({ quote }) => quote,
      quoteSession: ({ session }) => session.quote_journey_session,
      step: ({ quote_questions }) => quote_questions.step,
      loading: ({ page_loader }) => page_loader.show,
      fuelTypeList: ({ lookups }) => lookups.fuelTypes,
      supplierList: ({ lookups }) => lookups.suppliers,
      supplierGasList: ({ lookups }) => lookups.suppliersGas,
      supplierElectricityList: ({ lookups }) => lookups.suppliersElectricity,
      paymentMethodList: ({ lookups }) => lookups.paymentMethods,
      usageUnitsList: ({ lookups }) => lookups.usageUnits
    })
  },
  components: {
    YourHome,

    IceVehicle,
    ElectricVehicle,
    VehicleUsageDistance,

    PropertyType,
    PropertyUsage,
    ElectricVehicleOwnership,
    HeatingInsulation,

    PageHeader,
    ProgressBar
  }
};
</script>

<style lang="scss" scoped>
.page-content {
  padding-bottom: 160px;
}

.steps {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  max-width: 50%;
  margin: auto;

  div {
    flex: 1;
    height: 24px;
    margin: 0 7px;
    border: 2px solid black;

    &.active {
      background-color: $brand-colour-primary;
    }
  }
}
</style>
